// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import * as Actions from "../actions"
import { TimeGroup, UpdateTimeGroupRequest, } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import * as TimelineSlice from "../slices/timeline"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type UpdateTimeGroupRpc = (request: UpdateTimeGroupRequest) => Observable<TimeGroup>

export const updateTimeGroup = (updateTimeGroupRpc: UpdateTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.updateTimeGroup.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(updateTimeGroupRpc),
          map(t => t.toObject()),
          map(TimelineSlice.upsertTimeGroup),
          catchError(e => handleError(e, action.payload.getId())),
        )
      ),
    )

const updateTimeGroupRpc = (request: UpdateTimeGroupRequest): Observable<TimeGroup> =>
  grpc(() => timelineClient.updateTimeGroup(request, null))

const handleError = (e: unknown, timeGroupId: string): Observable<Action> => {
  const actions: Action[] = [
    TimelineSlice.setFailedTimeGroupId(timeGroupId),
  ]
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      // refresh the timeline since it's likely that we have a stale cache
      actions.push(showSnackBar({
        message: t({
          id: "msg_update_time_group_failed_not_found",
          message: "Failed to update time group. Could not find the target time group."
        }),
        messageType: "ERROR",
      }))
      actions.push(Actions.onTimelineOutOfSync())
      return concat(actions)
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      actions.push(showSnackBar({
        message: t({
          id: "msg_update_time_group_failed_permission_denied",
          message: "Failed to update time group. You do not have permission to update the time group."
        }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      // refresh the timeline since it's likely that we have a stale cache
      actions.push(showSnackBar({
        message: t({
          id: "msg_update_time_group_failed_failed_precondition",
          message: "Failed to update time group. The time group is already posted and can't be changed anymore."
        }),
        messageType: "ERROR",
      }))
      actions.push(Actions.onTimelineOutOfSync())
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({
      id: "msg_update_time_group_failed",
      message: "Failed to update the group. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

export default updateTimeGroup(updateTimeGroupRpc)
